import { TableFilter } from "@neonumy/ant-design";
import { clientEntities } from "@neonumy/api-clients";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Modal, Space } from "antd";
import Text from "antd/es/typography/Text";
import SelectAll from "components/SelectAll/SelectAll";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";

interface ValidateDebitsPropsTypes {
  id?: ID;
  refetch?: any;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
}
const paginationInit = {
  offset: 0,
  limit: 10,
  orderBy: ["-created_at"],
};
export default function ValidateDebits(props: ValidateDebitsPropsTypes): React.ReactElement {
  const { id, setIsModalVisible, isModalVisible, refetch } = props;
  const windowHeight = useGetWindowHeight();
  const { notificationError } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedRows, setSelectedRows] = useState<any>({
    selectedRowKeys: [],
    loading: false,
  });
  const { selectedRowKeys } = selectedRows;

  const {
    listSaleDocumentsOfDirectDebits,
    getAllDirectDebitsIds,
    refetchDirectDebits,
    loadingDirectDebits,
    listInvoicesOfDirectDebitsData: dataSource,
    validateDirectDebit,
    directDebitsTotalCount: totalCount,
  } = useDirectDebitApi();

  const { companyId }: RouteParams = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    if (isModalVisible) {
      listSaleDocumentsOfDirectDebits({
        variables: {
          id,
          ...paginationInit,
        },
      });
    }
  }, [isModalVisible]);

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedRows({
      selectedRowKeys: [],
    });
  };

  const handleOk = async () => {
    const response = await validateDirectDebit({
      variables: {
        id,
        documentsIds: selectedRows?.selectedRowKeys,
      },
    });

    const errors = response?.data?.validateDirectDebit?.errors;
    const success = response?.data?.validateDirectDebit?.success;
    if (success) {
      setIsModalVisible(false);
      setSelectedRows({
        selectedRowKeys: [],
      });
    } else {
      notificationError(errors);
    }
    refetch!();
  };

  const COLUMNS = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      key: "reference",
      title: t("Reference | Ref"),
      dataIndex: "reference",
    },
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      sorter: true,
      filterType: {
        type: "AsyncSelectFilter",
        varName: "group_ClientId",
        asyncSelectSettings: {
          translations: {
            label: t("Select client"),
            placeholder: t("Select an option"),
            loadingMessage: t("Loading"),
            noOptionsMessage: t("No options"),
            button: {
              search: t("Search"),
              reset: t("Reset"),
            },
          },
          hasQueryFilter: true,
          queryOptions: {
            client: clientEntities,
            dataName: "listEntities",
            query: LIST_CLIENTS_PAGINATED_SELECT,
            variables: {
              filter: {
                companyId,
                entityType: "CLIENT",
                search: "",
              },
              limit: 20,
              orderBy: ["name"],
            },
          },
        },
      },
      render: (_text: unknown, record: Record<string, any>) => {
        return <span>{`${record?.client?.name || ""}`}</span>;
      },
    },
    {
      key: "debt",
      title: t("Amount to be deducted"),
      dataIndex: "debt",
      render: (_text: unknown, record: Record<string, any>) => {
        return <span>{`${record?.debt || 0.0}€`}</span>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "4%" as const,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRows({
        ...selectedRows,
        selectedRowKeys: selectedRowKeys,
      });
    },
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetchDirectDebits({
      id,
      limit: size,
      offset: size * (page - 1),
    });
  };

  const handleOnClickSelectAll = async () => {
    const response = await getAllDirectDebitsIds({
      variables: {
        id,
      },
    });

    const allRowsKeys = response?.data?.listSaleDocumentsOfDirectDebits?.results?.map(
      item => item?.id,
    );

    setSelectedRows({
      selectedRowKeys: allRowsKeys,
    });
  };

  const handleOnClickReset = () => {
    setSelectedRows({
      selectedRowKeys: [],
    });
  };
  return (
    <Modal
      title={t("Validate payment")}
      open={isModalVisible}
      onOk={handleOk}
      okText={t("Save")}
      okButtonProps={{
        icon: (
          <span className="anticon">
            <FaSave />
          </span>
        ),
      }}
      onCancel={handleCancel}
      width={"70%"}
      className="antd-modal"
    >
      <SelectAll
        handleOnClickSelectAll={handleOnClickSelectAll}
        handleOnClickReset={handleOnClickReset}
        totalRowsSelected={Number(selectedRowKeys?.length)}
        totalRows={Number(totalCount)}
      />
      <TableFilter
        size="small"
        loading={loadingDirectDebits}
        rowSelection={rowSelection}
        columns={COLUMNS}
        dataSource={dataSource}
        scroll={{ y: windowHeight - 550 }}
        pagination={{
          onChange: handlePagination,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showTotal: total => t("Total table items", { count: total }),
        }}
      />
    </Modal>
  );
}
